import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PartyPopper, Loader2, CalendarCheck, CreditCard } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function PartyPayment() {
  const { id } = useParams();
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/parties/final-payment-page/${id}`)
      .then(({ data }) => setParty(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "This party could not be found."))
      .finally(() => setLoading(false));
  }, [id]);

  const proceed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parties/final-payment-page/${id}/checkout`);
      window.location.href = data.url; // single-use Stripe checkout
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      toast.error(msg);
      // If it's already paid, refresh so the "paid in full" screen shows.
      if (/already paid/i.test(msg || "")) {
        api.get(`/parties/final-payment-page/${id}`).then(({ data }) => setParty(data)).catch(() => {});
      }
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen"><Loader2 className="mx-auto animate-spin text-[#E0218A]" size={40} /></div>;
  }
  if (error) {
    return (
      <div data-testid="party-payment-error" className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen px-6">
        <h1 className="font-serif font-black text-4xl text-[#121212]">Hmm, something's off</h1>
        <p className="mt-4 text-[#121212]/70">{error}</p>
      </div>
    );
  }
  if (party.final_paid) {
    return (
      <div data-testid="party-payment-paid" className="pt-40 pb-32 text-center bg-[#F2E9D8] min-h-screen px-6">
        <PartyPopper className="mx-auto text-[#E0218A]" size={52} />
        <h1 className="font-serif font-black text-5xl text-[#121212] mt-6">Paid in full 🎉</h1>
        <p className="mt-4 text-[#121212]/80 text-lg max-w-xl mx-auto">Your party balance is already paid — there's nothing more to do. We can't wait to celebrate with you! We'll be in touch with any final details before your big day.</p>
      </div>
    );
  }

  const bd = party.breakdown || {};
  const amount = Number(party.amount || 0);
  const dateLine = [party.desired_date, party.preferred_time].filter(Boolean).join(" · ");

  return (
    <div data-testid="party-payment-page" className="fade-in bg-[#F2E9D8] min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-6 sm:px-10">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#E0218A]" />
          <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#E0218A]">Final party balance</span>
        </div>
        <h1 className="font-serif font-black text-[#121212] leading-[0.95] text-5xl sm:text-6xl">Almost party time, {party.name?.split(" ")[0] || "friend"}!</h1>

        <div data-testid="party-payment-summary" className="mt-8 bg-white border-2 border-[#121212] p-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60 inline-flex items-center gap-2"><CalendarCheck size={15} className="text-[#C6FF33]" /> Your celebration</p>
          <p className="font-serif font-black text-2xl text-[#121212] mt-2">{party.package || "Your party"}</p>
          {dateLine && <p className="text-[#121212]/80 mt-1">{dateLine}</p>}
        </div>

        {/* balance breakdown */}
        <div className="mt-8 bg-white border-2 border-[#121212] p-6" data-testid="party-payment-breakdown">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60">Balance breakdown</p>
          <div className="mt-3 text-sm space-y-1.5">
            <div className="flex justify-between"><span>{bd.package || "Party package"}</span><span>{money(bd.base)}</span></div>
            {bd.extra_seats > 0 && <div className="flex justify-between text-[#121212]/70"><span>Additional decorating guests ({bd.extra_seats} × {money(bd.extra_seat_fee)})</span><span>{money(bd.extra_guest_total)}</span></div>}
            {(bd.addons || []).map((a, i) => <div key={i} className="flex justify-between text-[#121212]/70"><span>{a.label} ({a.detail})</span><span>{money(a.amount)}</span></div>)}
            {(bd.manual_charges || []).map((m, i) => <div key={i} className="flex justify-between text-[#121212]/70"><span>{m.label}</span><span>{money(m.amount)}</span></div>)}
            {bd.discount_amount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount{bd.discount_type === "percent" ? ` (${bd.discount_value}%)` : ""}</span><span>−{money(bd.discount_amount)}</span></div>}
            <div className="flex justify-between font-bold border-t border-stone-300 pt-2 mt-2"><span>Total party cost</span><span>{money(bd.total)}</span></div>
            {bd.deposit_credit > 0 && <div className="flex justify-between text-[#121212]/70"><span>Deposit paid</span><span>−{money(bd.deposit_credit)}</span></div>}
          </div>
        </div>

        {/* pay */}
        <div className="mt-8 bg-white border-2 border-[#121212] p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#121212]/60">Balance due</p>
            <p data-testid="party-payment-amount" className="font-serif font-black text-3xl text-[#121212]">{money(amount)}</p>
          </div>
          <button
            data-testid="pay-final-balance-btn"
            onClick={proceed}
            disabled={busy || amount <= 0}
            className="mt-5 w-full px-8 py-4 bg-[#E0218A] text-white hover:bg-[#c41879] disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold transition-all inline-flex items-center justify-center gap-3"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : <><CreditCard size={16} /> Pay {money(amount)} balance</>}
          </button>
          <p className="text-xs text-[#121212]/60 mt-3 text-center">Secure checkout powered by Stripe. Your balance can only be paid once.</p>
        </div>
      </div>
    </div>
  );
}
